import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useNavigation } from '@react-navigation/core';
import { EntitySchema } from '@sierralabs/entity-core';
import { FormElementMeta, FormSectionMeta } from '@sierralabs/forms-core';
import {
  StateEventConfig,
  StateMachine,
  StateNode,
  StateNodeConfig
} from '@sierralabs/state-machine';

import { FetchError } from '../api/api';
import {
  ChangedFormValue,
  createDynamicFormContext,
  DynamicFormContextState,
  DynamicFormState,
  SubscribeTransitionFunc
} from './DynamicFormContext';
import { ActionPlugin } from './plugins/ActionPlugin';
import { defaultActionPlugins } from './plugins/actions/default';
import { getDefaultFormElementPlugins } from './plugins/form-elements/default';
import { FormElementPlugin } from './plugins/FormElementPlugin';
import { defaultTransitionConditionPlugins } from './plugins/transition-conditions/default';
import { TransitionConditionPlugin } from './plugins/TransitionConditionPlugin';
import { useUserContext } from '../context/UserContext';

export interface DynamicFormProviderProps<
  FormValuesType,
  FormElementConfigType
> {
  entitySchema?: EntitySchema;
  elementPlugins?: FormElementPlugin<any, any>[];
  actionPlugins?: ActionPlugin<any>[];
  transitionConditionPlugins?: TransitionConditionPlugin<any>[];
  formStateNode?: StateNodeConfig<
    FormSectionMeta<FormValuesType, FormElementConfigType>,
    any
  >;
  /**
   * Accessible from the form context (ex. used in the AssignFormAction plugin)
   */
  studyParticipantId?: number;
  values?: Partial<FormValuesType>;
  isEditSaving?: boolean;
  onLoadProgress?: (loadingElementQueue: FormElementMeta<any, any>[]) => void;
  onChange?: (values: Partial<FormValuesType>) => void;
  onFieldChange?: (
    key: keyof FormValuesType,
    value: FormValuesType[keyof FormValuesType]
  ) => void;
  children?: ReactNode;
}

/**
 * The Dynamic Form Context provides access to entity schema, form state, form
 * values, form edit state, and events when form is edited and changed. It is
 * not aware of form schema and form template entities to remain unopinionated
 * about the data source. See `FormDataSource` component on built in form data
 * storage.
 */
export const DynamicFormProvider = <FormValuesType, FormElementConfigType>({
  studyParticipantId,
  entitySchema,
  formStateNode,
  values,
  children,
  // isEditSaving,
  // onEditSchema,
  // onEdit,
  onLoadProgress,
  onChange,
  onFieldChange,
  ...props
}: React.PropsWithChildren<
  DynamicFormProviderProps<FormValuesType, FormElementConfigType>
>) => {
  if (formStateNode && !formStateNode.states) {
    formStateNode.states = [];
  }
  const { user } = useUserContext({ ignoreUndefined: true });
  const [transitioningStateEvent, setTransitioningStateEvent] =
    useState<StateEventConfig | null>();
  const onFieldChangeRef = useRef(onFieldChange);
  // setup state machine for handling form section transitions
  const elementPluginsRef = useRef<FormElementPlugin<any, any>[]>(
    props.elementPlugins || getDefaultFormElementPlugins()
  );
  const actionPluginsRef = useRef<ActionPlugin<any>[]>(
    props.actionPlugins || defaultActionPlugins
  );
  const transitionConditionPluginsRef = useRef<
    TransitionConditionPlugin<any>[]
  >(props.transitionConditionPlugins || defaultTransitionConditionPlugins);

  const machineOptions = useMemo(() => {
    const options = { actions: {}, guards: {} };
    for (const plugin of actionPluginsRef.current) {
      (options.actions as any)[plugin.type] = plugin.action;
    }
    for (const plugin of transitionConditionPluginsRef.current) {
      (options.guards as any)[plugin.type] = plugin.guard;
    }
    return options;
  }, []);
  const stateMachineRef = useRef(
    formStateNode
      ? new StateMachine<
          DynamicFormContextState<FormValuesType, FormElementConfigType>
        >(formStateNode, machineOptions)
      : undefined
  );

  // setup form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableEventTransition, setDisableEventTransition] = useState(false);
  const [error, setError] = useState<Error | FetchError>();
  const [formState, setFormState] = useState<
    DynamicFormState<FormValuesType, FormElementConfigType>
  >({
    lastFormElementType: 'TextField', // default to TextField
    isSubmitting: false
  });
  const [formValues, setFormValues] = useState<Partial<FormValuesType>>(
    values || {}
  );
  const [isScrollEnabled, setIsScrollEnabled] = useState(true);
  const previousFormValuesRef = useRef(formValues);

  // manage the active state nodes being displayed in DynamicForm component
  const [activeStateNodes, setActiveStateNodes] = useState<
    StateNode<FormSectionMeta<FormValuesType, any>, any>[]
  >([]);

  // notify loading progress
  const [loadingElementQueue, setLoadingElementQueue] = useState<
    FormElementMeta<any, any>[]
  >([]);
  const onLoadingProgressRef = useRef(onLoadProgress);
  useEffect(() => {
    onLoadingProgressRef.current?.(loadingElementQueue);
  }, [loadingElementQueue]);

  // changedFormValue is used by the data source to determine form value changes
  const [changedFormValue, setChangedFormValue] =
    useState<ChangedFormValue<FormValuesType>>();

  const clearFormValues = useCallback((keys?: (keyof FormValuesType)[]) => {
    if (keys && keys.length > 0) {
      setFormValues(formValues => {
        keys.forEach(key => {
          delete formValues[key];
        });
        return { ...formValues };
      });
    } else {
      setFormValues({});
    }
  }, []);

  const DynamicFormContext = createDynamicFormContext<
    FormValuesType,
    FormElementConfigType
  >();

  // handle form context event subscriptions
  type EventParam = Parameters<SubscribeTransitionFunc<any, any>>[0];
  type CallbackParam = Parameters<SubscribeTransitionFunc<any, any>>[1];
  const subscriptionsRef = useRef<
    { event: EventParam; callback: CallbackParam }[]
  >([]);
  const subscribe = useCallback(
    (event: EventParam, callback: CallbackParam) => {
      // prevent duplicate subscriptions
      if (
        !subscriptionsRef.current.some(
          s => s.event === event && s.callback === callback
        )
      ) {
        subscriptionsRef.current.push({ event, callback });
      }
    },
    []
  );
  const unsubscribe = useCallback(
    (event: EventParam, callback: CallbackParam) => {
      const index = subscriptionsRef.current.findIndex(
        s => s.event === event && s.callback === callback
      );
      if (index > -1) {
        subscriptionsRef.current.splice(index, 1);
      }
    },
    []
  );
  const formContextRef =
    useRef<DynamicFormContextState<FormValuesType, FormElementConfigType>>();
  const emitEvent = useCallback(async (event: EventParam, params: any) => {
    for (const subscription of subscriptionsRef.current) {
      if (subscription.event === event && formContextRef.current) {
        // console.log('emitEvent event', event, subscription);
        if (event) await subscription.callback(formContextRef.current, params);
      }
    }
  }, []);
  const transition = useCallback(
    (event?: StateEventConfig) => {
      // console.log('transitioning', event);
      if (
        subscriptionsRef.current.filter(s => s.event === 'transition')
          .length === 0
      ) {
        setIsSubmitting(true);
        // use the default state machine transition logic
        stateMachineRef.current?.transition(event?.name);
      } else {
        // DataSources and other application logic can override and handle their
        // own transition logic.
        setTransitioningStateEvent(event);
        emitEvent('transition', event);
      }
    },
    [emitEvent]
  );

  // used by the form elements to set form value changes
  const setFormValue = useCallback(
    async (
      key: keyof FormValuesType,
      value: FormValuesType[keyof FormValuesType],
      element?: FormElementMeta<FormValuesType, FormElementConfigType>
    ) => {
      // notify data sources that a form value has changed
      setChangedFormValue({ [key]: value });

      // set form value
      if (Array.isArray(formValues[key])) {
        // capture previous form value array for use in assign action
        (previousFormValuesRef.current[key] as any) = [
          ...(formValues[key] as any)
        ];
      }
      setFormValues(formValues => ({ ...formValues, [key]: value }));
      onFieldChangeRef.current?.(key, value);
      // run element action on form value change
      const state = stateMachineRef.current?.currentState;
      if (state) {
        state.context.formValues[key] = value;
      }
      if (element?.actions && state?.context) {
        for (const action of element.actions) {
          const plugin = actionPluginsRef.current.find(
            a => a.type === action.type
          );
          if (!plugin || !action.params) continue;
          try {
            await plugin.action(state, action.params);
          } catch (error) {
            console.error(error);
          }
        }
      }
      if (state) {
        const stateNode = stateMachineRef.current?.getStateNode(state.path);
        // run transition for stateNodes containing transient events
        if (stateNode?.events?.some(e => e.isTransient)) {
          // wait till state change propagates and then transition
          setTimeout(() => transition());
        }
      }
    },
    [formValues, transition]
  );
  const navigation = useNavigation();
  const formContext: DynamicFormContextState<
    FormValuesType,
    FormElementConfigType
  > = useMemo(
    () => ({
      user: user ?? undefined,
      studyParticipantId,
      entitySchema,
      formState,
      formSections: formStateNode?.states || [],
      loadingElementQueue: loadingElementQueue,
      setLoadingElementQueue,
      isSubmitting,
      error,
      formValues,
      isScrollEnabled,
      previousFormValues: previousFormValuesRef.current,
      changedFormValue,
      stateMachine: stateMachineRef.current,
      transitioningStateEvent,
      setTransitioningStateEvent,
      setFormState,
      setFormValues,
      setFormValue,
      clearFormValues,
      setIsSubmitting,
      setError,
      setIsScrollEnabled,
      elementPlugins: elementPluginsRef.current,
      actionPlugins: actionPluginsRef.current,
      transitionConditionPlugins: transitionConditionPluginsRef.current,
      navigation,
      subscribe,
      unsubscribe,
      emitEvent,
      transition,
      activeStateNodes,
      setActiveStateNodes,
      disableEventTransition,
      setDisableEventTransition
    }),
    [
      user,
      studyParticipantId,
      changedFormValue,
      clearFormValues,
      emitEvent,
      entitySchema,
      formState,
      formStateNode?.states,
      loadingElementQueue,
      isSubmitting,
      error,
      formValues,
      navigation,
      setFormValue,
      subscribe,
      transitioningStateEvent,
      unsubscribe,
      transition,
      isScrollEnabled,
      activeStateNodes,
      disableEventTransition,
      setDisableEventTransition
    ]
  );
  // stor reference to form context for use in emitEvent()
  formContextRef.current = formContext;
  // handle form value changes and event propagation
  useEffect(() => {
    if (formValues && formValues !== previousFormValuesRef.current) {
      previousFormValuesRef.current = formValues;
      // update state machine context values
      const stateMachine = stateMachineRef.current;
      if (
        stateMachine?.isStarted &&
        !stateMachine?.isTransitioning &&
        stateMachine?.currentState
      ) {
        stateMachine.currentState.context = formContext;
      }
      onChange?.(formValues);
    }
  }, [formContext, formValues, onChange]);
  return (
    <DynamicFormContext.Provider value={formContext}>
      {children}
    </DynamicFormContext.Provider>
  );
};

export default DynamicFormProvider;
